import { ENV, PROD_ENV } from "../Constants/constants";

/**
 * Adobe Analytics data layer (window.digitalData).
 *
 * The Adobe Launch script itself is loaded globally in index.tsx via
 * REACT_APP_ADOBE_SCRIPT. This module only builds/refreshes the data layer
 * that Launch reads, and re-fires a virtual page view on each SPA route change.
 *
 * NOTE: pageName / siteSection below are a PROPOSED mapping — please review and
 * correct against the analytics spec.
 */

const SITE_PREFIX = "ziplyfiber-myaccount";
const SITE_TYPE = "myaccount";
const SITE_DOMAIN = "ziplyfiber.com";

// REACT_APP_ENV values: production | Staging | Devlopment | Sit
const SITE_ENV_MAP: Record<string, string> = {
  production: "prod",
  Staging: "stg",
  Devlopment: "dev",
  Sit: "sit",
};

const getSiteEnv = (): string =>
  ENV === PROD_ENV ? "prod" : (SITE_ENV_MAP[ENV] ?? "stg");

// Ordered most-specific first so nested paths match before their parents.
// [pathMatcher, pageNameSlug, siteSection]
const PAGE_MAP: Array<[RegExp, string, string]> = [
  [/^\/home\/speedtest/, "speedtest", "home"],
  [/^\/home\/reschedule-appointment/, "reschedule-appointment", "home"],
  [/^\/home\/cancel-appointment/, "cancel-appointment", "home"],
  [/^\/home/, "home", "home"],

  [/^\/billing\/makeapayment/, "makeapayment", "billing"],
  [/^\/billing\/signupautopay/, "signup-autopay", "billing"],
  [/^\/billing\/manageautopay/, "manage-autopay", "billing"],
  [/^\/billing/, "billing", "billing"],

  [/^\/account\/manageautopay/, "manage-autopay", "account"],
  [/^\/account\/signupautopay/, "signup-autopay", "account"],
  [/^\/account\/upgrade-speed/, "upgrade-speed", "account"],
  [/^\/account\/orderandservicetickethistory/, "order-service-ticket-history", "account"],
  [/^\/account\/nutritionlabel/, "nutrition-label", "account"],
  [/^\/account/, "account", "account"],

  [/^\/messages/, "messages", "messages"],
  [/^\/select-account/, "select-account", "login"],
  [/^\/commercial/, "commercial", "commercial"],

  [/^\/payment\/success/, "payment-success", "payment"],
  [/^\/payment\/(failed|failure)/, "payment-failed", "payment"],
  [/^\/payment/, "payment", "payment"],

  [/^\/login/, "login", "login"],
  [/^\/register/, "register", "register"],
  [/^\/checking-account/, "checking-account", "login"],
  [/^\/acc-not-found/, "account-not-found", "account"],
  [/^\/get-token/, "get-token", "login"],

  [/^\/adminscreen/, "adminscreen", "admin"],
  [/^\/admin\/login/, "admin-login", "admin"],
];

const resolvePageInfo = (pathname: string): { pageName: string; siteSection: string } => {
  const match = PAGE_MAP.find(([pattern]) => pattern.test(pathname));
  const slug = match ? match[1] : "not-found";
  const siteSection = match ? match[2] : "other";
  return { pageName: `${SITE_PREFIX}-${slug}`, siteSection };
};

// Adobe Experience Cloud ID (ECID), only available once Launch has loaded.
const getEcid = (): string => {
  const w = window as any;
  try {
    return w?._satellite?.getVisitorId?.()?.getMarketingCloudVisitorID?.() || "";
  } catch {
    return "";
  }
};

interface PageViewInput {
  pathname: string;
  /** "customer" once authenticated / an account is resolved, else "prospect". */
  visitorType?: string;
  /** Fallback identifier (e.g. contactId / accountNumber) if ECID isn't ready. */
  visitorId?: string;
}

/**
 * Rebuilds window.digitalData for the current route and notifies Adobe Launch of
 * the virtual page view. Called on every route change from Routes.tsx.
 */
export const pushPageView = ({ pathname, visitorType, visitorId }: PageViewInput): void => {
  const w = window as any;
  const { pageName, siteSection } = resolvePageInfo(pathname);

  w.digitalData = w.digitalData || {};
  w.digitalData.page = {
    pageInfo: {
      pageName,
      siteSection,
      siteEnv: getSiteEnv(),
    },
  };
  w.digitalData.session = {
    siteType: SITE_TYPE,
    siteDomain: SITE_DOMAIN,
  };
  w.digitalData.userData = {
    // TODO: client IP has no source in this SPA — populate from an API/header if required.
    IPaddress: "",
    visitorType: visitorType || "prospect",
    visitorID: getEcid() || visitorId || "",
  };
  w.digitalData.events = w.digitalData.events || {};

  // On SPA route changes Launch won't re-run its page-load rule automatically,
  // so trigger a direct-call rule ("pageView") after the data layer is refreshed.
  if (typeof w?._satellite?.track === "function") {
    w._satellite.track("pageView");
  }
};
